/** @jsx jsx */
/** @jsxFrag null */
// eslint-disable-next-line no-restricted-imports
import {jsx, ParamComponent} from 'panel';
import isEqual from 'lodash/isEqual';

import {IronContextRegistry} from 'common/report/context-registry';
import {
  SegmentFilterController,
  SelectMode,
  ToggleCallbackArgs,
} from 'common/charts/contexts/segment-filter-controller';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {InsightsBookmarkParams} from 'common/report/insights/util/types';
import {ThemesResource} from 'common/report/model-resources/themes';

import './index.styl';

type ChartRootJsxProps = {
  params?: {
    bookmarkParams?: InsightsBookmarkParams;
    onSegmentFilterChange?: (
      segmentFilterController: SegmentFilterController,
      toggleCallbackArgs: ToggleCallbackArgs,
    ) => void;
  };
};

/**
 * This component serves as the context provider for all chart types.
 *
 * It is important that every chart creates a new instance of `SegmentFilterController`
 * since legend & table data are local to each chart. SegmentFilterController must never
 * be initialized at the root level.
 *
 * @todo remove `segmentFilterController` from IronContextRegistry so that the above requirement
 * is type enforced
 */
export class ChartRoot extends ParamComponent<ChartRootJsxProps['params'], EmptyObj, IronContextRegistry> {
  get config() {
    return {
      template: () => <div />,
      contexts: [`segmentFilterController` as const, `currentProject` as const, `themes` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
      params: {
        bookmarkParams: Object,
        onSegmentFilterChange: Function,
      },
      hooks: {
        preUpdate: (_newState, newParams?: ChartRootJsxProps['params']) => {
          if (!newParams) {
            return;
          }

          if (newParams.bookmarkParams) {
            this.segmentFilterController.setReportTheme(newParams.bookmarkParams?.displayOptions?.theme ?? null);

            /** makes sure legend selection changes to "Top N" when segments change */
            if (
              this.params.bookmarkParams &&
              this.shouldResetSegmentFilterSelectMode(this.params.bookmarkParams, newParams.bookmarkParams)
            ) {
              this.segmentFilterController.selectMode = SelectMode.Position;
            }
          }
        },
      },
    };
  }

  resourceCustomSubscriptions = new Map([
    [
      ThemesResource,
      () => {
        this.getContext(`segmentFilterController`).updateConnectedComponents();
        this.update();
      },
    ],
  ]);

  connectedCallback(): void {
    this.segmentFilterController.themesResource = this.getContext(`themes`);
    super.connectedCallback();

    this.segmentFilterController.subscribeToggle(this.handleSegmentFilterChange);

    if (this.params.bookmarkParams) {
      // this was needed in download mode (?download=1) because the preUpdate hook is never run
      this.segmentFilterController.setReportTheme(this.params.bookmarkParams?.displayOptions?.theme ?? null);
    }
  }

  disconnectedCallback() {
    this.segmentFilterController.unsubscribeToggle(this.handleSegmentFilterChange);
    super.disconnectedCallback();
  }

  get segmentFilterController() {
    return this.getContext(`segmentFilterController`);
  }

  handleSegmentFilterChange = (toggleCallbackArgs: ToggleCallbackArgs) => {
    if (this.params.onSegmentFilterChange) {
      this.params.onSegmentFilterChange(this.segmentFilterController, toggleCallbackArgs);
    }
  };

  shouldResetSegmentFilterSelectMode(original: InsightsBookmarkParams, edited: InsightsBookmarkParams) {
    if (
      original.sections?.cohorts &&
      original.sections?.group &&
      edited?.sections?.cohorts &&
      edited?.sections?.group
    ) {
      const cohortsChanged = !isEqual(original.sections.cohorts, edited.sections.cohorts);
      const groupsChanged = !isEqual(original.sections.group, edited.sections.group);

      return cohortsChanged || groupsChanged;
    }

    return false;
  }
}

export const ChartRootEl = defineAndMakeJsxFactory<ChartRootJsxProps>(`mp-chart-root`, ChartRoot);
export const ChartRootReact = panel2react<ChartRootJsxProps>(`mp-chart-root`, ChartRoot);
